/**
 * Maryland State-Specific Diagnostic Questions
 * CORRECTED: All citations reference COMAR 10.07.05 (Residential Service Agencies)
 * NOTE: COMAR 10.07.10 is for HOME HEALTH AGENCIES (different license type)
 * Regulatory Body: Maryland Department of Health - Office of Health Care Quality (OHCQ)
 * Last Updated: December 2025
 * 
 * Key Maryland-Specific Elements:
 * - RSA license valid for 3 YEARS (not 2)
 * - License fee: $1,000 per 3-year period
 * - CJIS + FBI fingerprint background checks required
 * - 5-year record retention for client records (COMAR 10.07.05.15)
 * - 7-year retention for medical records under Health-General §4-403
 * - APS Hotline: 1-800-917-7383
 * - Maryland Family Law Article 14-302 (vulnerable adult protection)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MARYLAND_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'MD-A1',
    itemId: 'A1',
    stateCode: 'MD',
    title: 'Residential Service Agency License Status',
    description: 'Maryland requires all RSAs to be licensed by MDH-OHCQ under COMAR 10.07.05',
    severity: 'high',
    regulationCode: '10.07.05.04',
    regulationName: 'Licensure',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq/rsa/Pages/home.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Residential Service Agency (RSA) license from MDH-OHCQ?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'RSA licenses are valid for 3 YEARS. Initial fee is $1,000.',
      },
      {
        id: 'q2',
        question: 'What is your RSA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your RSA license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What level of RSA license do you hold?',
        inputType: 'select',
        options: [
          { value: 'level1', label: 'Level 1: ADLs + medication reminders (no administration)' },
          { value: 'level2', label: 'Level 2: ADLs + medication administration (CMT/LPN under RN)' },
          { value: 'level3', label: 'Level 3: Complex care (IV, trach, ventilator) by RN/LPN' },
          { value: 'other', label: 'RSA-Other (skilled disciplines only, no aides)' },
          { value: 'dme', label: 'RSA-Other DME (durable medical equipment)' },
        ],
        required: true,
        helpText: 'Levels 2 and 3 are eligible for Maryland Medicaid waiver billing',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 180, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your MDH-OHCQ Residential Service Agency license is current per COMAR 10.07.05.',
      atRisk: 'Your RSA license expires within 90 days. Submit renewal to MDH-OHCQ immediately. RSA licenses are valid for 3 years.',
      nonCompliant: 'Operating an RSA without a valid MDH license violates COMAR 10.07.05 and Health-General Article, Title 19, Subtitle 4A. Contact OHCQ at (410) 402-8040.',
      unknown: 'Verify your RSA license status with MDH-OHCQ at (410) 402-8040.',
    },
    stateNotes: [
      'RSA licenses are valid for 3 YEARS (not 2)',
      'License fee: $1,000 (non-refundable)',
      'RSAs are NOT eligible for Medicare (except DME)',
      'Different from Home Health Agency (HHA) license under COMAR 10.07.10',
      'No Certificate of Need (CON) required for RSA (unlike HHA)',
    ],
  },

  {
    id: 'MD-A2',
    itemId: 'A2',
    stateCode: 'MD',
    title: 'Written Policies and Procedures',
    description: 'Maryland requires specific written policies per COMAR 10.07.05.08',
    severity: 'high',
    regulationCode: '10.07.05.08',
    regulationName: 'Policies',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Maryland-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Client rights and responsibilities' },
          { value: 'service_agreement', label: 'Written service agreement requirements' },
          { value: 'personnel', label: 'Personnel policies and job descriptions' },
          { value: 'supervision', label: 'Supervision of direct care workers' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Background check procedures (CJIS/FBI)' },
          { value: 'training', label: 'Training and competency requirements' },
          { value: 'abuse_reporting', label: 'Abuse reporting procedures (APS)' },
          { value: 'emergency', label: 'Emergency and disaster preparedness' },
          { value: 'infection', label: 'Infection control program' },
          { value: 'records', label: 'Record retention (5 years per COMAR 10.07.05.15)' },
        ],
        required: true,
        helpText: 'Policies must be submitted with license application per COMAR 10.07.05.04(A)(2)(i)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Maryland COMAR 10.07.05.08 requirements.',
      atRisk: 'Your policy manual is missing required sections per COMAR 10.07.05.08.',
      nonCompliant: 'Multiple required policies are missing per COMAR 10.07.05.08. This is a common OHCQ survey deficiency.',
      unknown: 'Review your policies against COMAR 10.07.05.08 requirements.',
    },
    stateNotes: [
      'Policies must be submitted with initial license application per COMAR 10.07.05.04(A)(2)(i)',
      'Written policies must be reviewed and updated at least annually',
      'Personnel policies must include job descriptions per COMAR 10.07.05.10',
      'Client rights policies must comply with COMAR 10.07.05.16',
      'OHCQ surveys specifically review policy documentation for completeness',
    ],
  },

  {
    id: 'MD-A3',
    itemId: 'A3',
    stateCode: 'MD',
    title: 'Workers\' Compensation Insurance',
    description: 'Maryland requires Workers\' Compensation for all employees',
    severity: 'high',
    regulationCode: '10.07.05.04',
    regulationName: 'Workers Compensation',
    agencyName: 'Maryland Workers\' Compensation Commission',
    agencyAbbr: 'WCC',
    agencyPhone: '(410) 864-5100',
    agencyUrl: 'https://www.wcc.state.md.us/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current Workers\' Compensation insurance as required by the Maryland Workers\' Compensation Commission?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, current coverage' },
          { value: 'exempt_corp', label: 'Exempt - Corporation/LLC with Certificate of Compliance' },
          { value: 'exempt_sole', label: 'Exempt - Sole proprietorship/partnership with no employees' },
          { value: 'no', label: 'No coverage' },
        ],
        required: true,
        helpText: 'Required for RSA license per COMAR 10.07.05.04(A)(2)(j)',
      },
      {
        id: 'q2',
        question: 'When does your Workers\' Compensation policy expire?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['yes', 'exempt_corp', 'exempt_sole'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers\' Compensation insurance meets Maryland requirements.',
      atRisk: 'Workers\' Compensation may be expiring soon. Verify coverage status.',
      nonCompliant: 'Workers\' Compensation is required per COMAR 10.07.05.04(A)(2)(j). Contact WCC at (410) 864-5100.',
      unknown: 'Verify Workers\' Compensation status.',
    },
    stateNotes: [
      'Proof of workers\' compensation required for RSA license per COMAR 10.07.05.04(A)(2)(j)',
      'Maryland Workers\' Compensation Act applies to employers with 1+ employees',
      'Sole proprietors and partners may elect coverage for themselves',
      'Failure to maintain coverage can result in license revocation',
      'Certificate of Compliance available for exempt corporations/LLCs',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'MD-B1',
    itemId: 'B1',
    stateCode: 'MD',
    title: 'Criminal Background Check (CJIS)',
    description: 'Maryland requires CJIS criminal background checks through DPSCS',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'Background Check',
    agencyName: 'Maryland Department of Public Safety - CJIS',
    agencyAbbr: 'CJIS',
    agencyPhone: '(410) 764-4501',
    agencyUrl: 'https://www.dpscs.state.md.us/publicservs/bgchecks.shtml',
    questions: [
      {
        id: 'q1',
        question: 'Do you obtain CJIS (Maryland State) criminal background checks for ALL direct care workers?',
        inputType: 'select',
        options: [
          { value: 'always_before', label: 'Yes, always before any client contact' },
          { value: 'sometimes', label: 'Sometimes / not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(B)(1)(d)',
      },
      {
        id: 'q2',
        question: 'Do you also obtain FBI fingerprint checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Federal requirement for health care providers',
      },
      {
        id: 'q3',
        question: 'Do any workers begin providing services BEFORE their background check is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always_before' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet COMAR 10.07.05.10 requirements.',
      atRisk: 'Background check process may be incomplete. Maryland requires CJIS + FBI checks before client contact.',
      nonCompliant: 'Workers providing services without background clearance or starting work before clearance is received violates COMAR 10.07.05.10. Employees working before background check clearance is a direct regulatory violation. This can result in license revocation.',
      unknown: 'Verify background check procedures with CJIS at (410) 764-4501.',
    },
    stateNotes: [
      'CJIS (Maryland State) and FBI fingerprint checks required per COMAR 10.07.05.10(B)(1)(d)',
      'Background checks must be completed BEFORE any client contact',
      'Fingerprinting available through Maryland CJIS approved vendors',
      'Results must be maintained in personnel file for duration of employment',
      'Disqualifying offenses include crimes of violence, theft, and certain drug offenses',
    ],
  },

  {
    id: 'MD-B2',
    itemId: 'B2',
    stateCode: 'MD',
    title: 'Nurse Aide Registry Check',
    description: 'Maryland requires checking the Board of Nursing Nurse Aide Registry',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'Registry Check',
    agencyName: 'Maryland Board of Nursing',
    agencyAbbr: 'MBON',
    agencyPhone: '(410) 585-1900',
    agencyUrl: 'https://mbon.maryland.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential employees against the Maryland Nurse Aide Registry before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes, but not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(B)(1)(e)',
      },
      {
        id: 'q2',
        question: 'Do you document registry check results in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Nurse Aide Registry checks are completed and documented for all employees.',
      atRisk: 'Registry checks may not be consistently performed or documented.',
      nonCompliant: 'Failure to check the Nurse Aide Registry violates COMAR 10.07.05.10(B)(1)(e). Contact MBON at (410) 585-1900.',
      unknown: 'Verify registry check process is in place.',
    },
    stateNotes: [
      'Nurse Aide Registry check required per COMAR 10.07.05.10(B)(1)(e)',
      'Registry available online at mbon.maryland.gov',
      'Check for abuse, neglect, or misappropriation findings',
      'Document date of check and results in personnel file',
      'Must be verified before hire and documented with printout or screenshot',
    ],
  },

  {
    id: 'MD-B3',
    itemId: 'B3',
    stateCode: 'MD',
    title: 'OIG/SAM Exclusion Check',
    description: 'Federal exclusion list verification required',
    severity: 'high',
    regulationCode: '42-1320a-7',
    regulationName: 'LEIE',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check OIG LEIE and SAM.gov exclusion lists for all staff?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'OIG recommends monthly verification',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'quarterly', result: 'AT_RISK' },
      { condition: 'includes', value: ['hire_only', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion checks are conducted monthly.',
      atRisk: 'OIG recommends monthly verification, not quarterly. Increase frequency.',
      nonCompliant: 'OIG/SAM exclusion checks are required and should be conducted monthly.',
      unknown: 'Verify OIG/SAM exclusion check procedures.',
    },
    stateNotes: [
      'OIG LEIE check required at exclusions.oig.hhs.gov for all staff',
      'SAM.gov check required for vendors and contractors',
      'OIG recommends monthly verification for Medicare/Medicaid providers',
      'Employing excluded individuals can result in civil monetary penalties',
      'Document all checks with date and results in personnel file',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL FILES & TRAINING
  // ============================================
  {
    id: 'MD-C1',
    itemId: 'C1',
    stateCode: 'MD',
    title: 'Personnel File Requirements',
    description: 'Maryland requires specific documents in each employee file',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'Personnel',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY direct care worker\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application/resume' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'cjis', label: 'CJIS criminal background check clearance' },
          { value: 'fbi', label: 'FBI fingerprint check clearance' },
          { value: 'registry', label: 'Nurse Aide Registry check' },
          { value: 'license', label: 'Professional license verification (if applicable)' },
          { value: 'tb', label: 'TB screening/clearance' },
          { value: 'health', label: 'Basic health screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'training', label: 'Training and orientation documentation' },
          { value: 'competency', label: 'Competency/skills assessment records' },
          { value: 'job_desc', label: 'Signed job description' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(B)(1)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 8, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Maryland-required documentation per COMAR 10.07.05.10.',
      atRisk: 'Some personnel files are missing required documents. Review COMAR 10.07.05.10(B)(1).',
      nonCompliant: 'Personnel files are missing critical documentation. OHCQ surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against COMAR 10.07.05.10 requirements.',
    },
    stateNotes: [
      'Personnel records must be maintained per COMAR 10.07.05.10(B)(1)',
      'CJIS and FBI background checks required before client contact',
      'TB screening must be completed prior to employment',
      'I-9 required under federal law within 3 days of hire',
      'Training documentation must include dates and topics covered',
    ],
  },

  {
    id: 'MD-C2',
    itemId: 'C2',
    stateCode: 'MD',
    title: 'Employee Training Requirements',
    description: 'Maryland requires orientation and in-service training for direct care workers',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'Training',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new direct care workers complete orientation BEFORE any client contact?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first client' },
          { value: 'usually', label: 'Usually, but sometimes start early' },
          { value: 'no', label: 'No formal orientation requirement' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(B)(1)(f)',
      },
      {
        id: 'q2',
        question: 'What does your orientation cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'rights', label: 'Client rights and confidentiality' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'infection', label: 'Infection control practices' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'hipaa', label: 'HIPAA privacy training' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you complete skills assessment and demonstration before client referral?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(B)(1)(i)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countLessThan', questionId: 'q2', value: 6 },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Training program meets COMAR 10.07.05.10 requirements.',
      atRisk: 'Training may be incomplete or workers starting before full orientation.',
      nonCompliant: 'Workers providing services without proper training violates COMAR 10.07.05.10.',
      unknown: 'Review training program against COMAR 10.07.05.10 requirements.',
    },
    stateNotes: [
      'Orientation must be completed BEFORE first client contact per COMAR 10.07.05.10(B)(1)(f)',
      'Skills competency assessment required per COMAR 10.07.05.10(B)(1)(i)',
      'Training must include abuse recognition and mandatory reporting',
      'HIPAA privacy training required under federal law',
      'Document all training with dates, topics, and employee signature',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'MD-D1',
    itemId: 'D1',
    stateCode: 'MD',
    title: 'TB Screening Requirements',
    description: 'Maryland requires tuberculosis screening for all direct care workers',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'TB Screening',
    agencyName: 'Maryland Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers have TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all workers have current TB screening' },
          { value: 'most', label: 'Most do, some may need updating' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'TB testing must be completed BEFORE client contact per COMAR 10.07.05.10(B)(1)(c)',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all workers.',
      atRisk: 'Some TB screenings may need updating. Review all worker files.',
      nonCompliant: 'Workers providing services without TB screening violates COMAR 10.07.05.10(B)(1)(c).',
      unknown: 'Verify TB screening status for all workers.',
    },
    stateNotes: [
      'TB testing must be completed BEFORE client contact per COMAR 10.07.05.10(B)(1)(c)',
      'Acceptable methods: tuberculin skin test (TST) or IGRA blood test',
      'Positive results require chest X-ray and physician clearance',
      'Annual screening may be required based on agency policy',
      'Document results in personnel file with date and provider signature',
    ],
  },

  // ============================================
  // SECTION E: PATIENT CARE
  // ============================================
  {
    id: 'MD-E1',
    itemId: 'E1',
    stateCode: 'MD',
    title: 'Plan of Care Requirements',
    description: 'Maryland requires written service plans for all clients',
    severity: 'high',
    regulationCode: '10.07.05.12',
    regulationName: 'Plan of Care',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.12(C)',
      },
      {
        id: 'q2',
        question: 'Does an RN assess each new client who requires skilled services and assistance with ADLs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.12(B)(1)',
      },
      {
        id: 'q3',
        question: 'Are care plans reviewed when appropriate by an RN or authorized health practitioner?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.12(C)(5)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plan of care procedures meet COMAR 10.07.05.12 requirements.',
      atRisk: 'Care plans may not be consistently created before services or reviewed properly.',
      nonCompliant: 'Services being provided without proper care plans violates COMAR 10.07.05.12.',
      unknown: 'Review care plan process against COMAR 10.07.05.12 requirements.',
    },
    stateNotes: [
      'Written plan of care required BEFORE services begin per COMAR 10.07.05.12(C)',
      'RN assessment required for clients needing skilled services per COMAR 10.07.05.12(B)(1)',
      'Care plans must be reviewed by RN or authorized health practitioner per COMAR 10.07.05.12(C)(5)',
      'Plan must include specific services, frequency, and goals',
      'Client/representative signature required on care plan',
    ],
  },

  {
    id: 'MD-E2',
    itemId: 'E2',
    stateCode: 'MD',
    title: 'Supervision of Direct Care Workers',
    description: 'Maryland requires RN supervision of aides providing services',
    severity: 'high',
    regulationCode: '10.07.05.12',
    regulationName: 'Supervision',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'How often does an RN supervise aides providing services in the home?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'At least every 60 days' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'as_needed', label: 'As needed / no schedule' },
          { value: 'never', label: 'No RN supervision' },
        ],
        required: true,
        helpText: 'COMAR 10.07.05.12(E)(2)(c) requires at least every 60 days',
      },
      {
        id: 'q2',
        question: 'Do you document the supervision provided by the registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.12(E)(3)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_60' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'never' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_90' },
        { condition: 'equals', questionId: 'q1', value: 'as_needed' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets COMAR 10.07.05.12(E) requirements.',
      atRisk: 'Supervision may not be at required 60-day intervals or documentation is lacking.',
      nonCompliant: 'No RN supervision violates COMAR 10.07.05.12(E). Implement supervision immediately.',
      unknown: 'Review supervision procedures against COMAR 10.07.05.12(E).',
    },
    stateNotes: [
      'RN supervisory visit required at least every 60 days per COMAR 10.07.05.12(E)(2)(c)',
      'Supervision must be documented in client record per COMAR 10.07.05.12(E)(3)',
      'RN must assess aide performance and client status during visit',
      'For Level 2/3 RSAs, supervision may be more frequent based on client needs',
      'Documentation must include date, findings, and any corrective actions',
    ],
  },

  // ============================================
  // SECTION F: DOCUMENTATION & RECORDS
  // ============================================
  {
    id: 'MD-F1',
    itemId: 'F1',
    stateCode: 'MD',
    title: 'Clinical Record Requirements',
    description: 'Maryland requires specific clinical record documentation',
    severity: 'high',
    regulationCode: '10.07.05.14',
    regulationName: 'Clinical Records',
    agencyName: 'Maryland Department of Health - Office of Health Care Quality',
    agencyAbbr: 'MDH-OHCQ',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'What elements are included in your client clinical records? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'demographics', label: 'Client demographics and emergency contacts' },
          { value: 'medical_hx', label: 'Medical history and current conditions' },
          { value: 'plan_care', label: 'Plan of care' },
          { value: 'consent', label: 'Informed consent forms' },
          { value: 'visit_notes', label: 'Visit notes documenting services provided' },
          { value: 'assessments', label: 'Assessments and reassessments' },
          { value: 'supervision', label: 'Supervision documentation' },
          { value: 'medications', label: 'Medication records (if applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain required documentation per COMAR 10.07.05.14.',
      atRisk: 'Clinical records may be missing some required elements.',
      nonCompliant: 'Clinical records are missing critical documentation required by COMAR 10.07.05.14.',
      unknown: 'Review clinical records against COMAR 10.07.05.14.',
    },
    stateNotes: [
      'Clinical records must be maintained per COMAR 10.07.05.14',
      'Records must include demographics, emergency contacts, and medical history',
      'Plan of care and all assessments must be documented',
      'Visit notes required for each service provided',
      'Records must be secured and accessible only to authorized personnel',
    ],
  },

  {
    id: 'MD-F2',
    itemId: 'F2',
    stateCode: 'MD',
    title: 'Record Retention',
    description: 'Maryland requires 5-year client record retention; 7 years for medical records',
    severity: 'high',
    regulationCode: '10.07.05.15',
    regulationName: 'Record Retention',
    agencyName: 'Maryland Department of Health',
    agencyAbbr: 'MDH',
    agencyPhone: '(410) 402-8040',
    agencyUrl: 'https://health.maryland.gov/ohcq',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client records after discharge?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets both requirements)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'COMAR 10.07.05.15 requires 5 years; Health-General §4-403 requires 7 years for medical records',
      },
      {
        id: 'q2',
        question: 'Do you have procedures for record retention if your agency ceases operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.15(B)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['7plus', '5to6'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Maryland requirements.',
      atRisk: 'Retention policy or closure procedures may be incomplete. Review COMAR 10.07.05.15.',
      nonCompliant: 'Record retention does not meet Maryland requirements. Minimum 5 years (7 years for medical records under Health-General §4-403).',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'COMAR 10.07.05.15: 5 years after discharge',
      'For minors: until age 21 or 5 years after record made, whichever is later',
      'Health-General §4-403: 7 years for medical records, lab reports, X-rays',
      'For minors: until age 25 (18 + 7 years)',
    ],
  },

  // ============================================
  // SECTION G: REPORTING & QUALITY
  // ============================================
  {
    id: 'MD-G1',
    itemId: 'G1',
    stateCode: 'MD',
    title: 'Abuse/Neglect Reporting',
    description: 'Maryland requires mandatory reporting of abuse/neglect to APS',
    severity: 'high',
    regulationCode: '14-302',
    regulationName: 'Abuse Reporting',
    agencyName: 'Maryland Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(800) 917-7383',
    agencyUrl: 'https://dhs.maryland.gov/office-of-adult-services/adult-protective-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers receive training on abuse recognition and mandatory reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all workers are trained' },
          { value: 'most', label: 'Most workers are trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do workers know the APS reporting hotline number (1-800-917-7383)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
        helpText: '1-800-91-PREVENT (1-800-917-7383)',
      },
      {
        id: 'q3',
        question: 'Are workers aware they are mandatory reporters under Maryland Family Law Article 14-302?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'All workers are trained on abuse recognition and mandatory reporting per Maryland Family Law Article 14-302.',
      atRisk: 'Some workers may lack proper abuse reporting training or knowledge of reporting procedures.',
      nonCompliant: 'Failure to train workers on mandatory reporting requirements violates Maryland law. Report abuse to APS at (800) 917-7383.',
      unknown: 'Verify all workers have received abuse recognition and reporting training.',
    },
    stateNotes: [
      'APS Hotline: 1-800-91-PREVENT (1-800-917-7383)',
      'Mandatory reporters include health practitioners and human service workers',
      'Good faith reporters are immune from civil liability (Family Law 5-708)',
      'Failure to report can result in criminal penalties',
      'Reports serve adults 18+ who lack capacity to provide for their daily needs',
    ],
  },

  {
    id: 'MD-G2',
    itemId: 'G2',
    stateCode: 'MD',
    title: 'Board of Nursing Reporting',
    description: 'Maryland requires reporting certain actions to the Board of Nursing',
    severity: 'high',
    regulationCode: '10.07.05.10',
    regulationName: 'MBON Reporting',
    agencyName: 'Maryland Board of Nursing',
    agencyAbbr: 'MBON',
    agencyPhone: '(410) 585-1900',
    agencyUrl: 'https://mbon.maryland.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have procedures to report actions or conditions that may be grounds for Board of Nursing action to MBON?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not aware of requirement' },
        ],
        required: true,
        helpText: 'Required per COMAR 10.07.05.10(F) - must report immediately when known',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Board of Nursing reporting procedures are in place per COMAR 10.07.05.10(F).',
      atRisk: 'No procedures for MBON reporting. Review COMAR 10.07.05.10(F) requirements.',
      nonCompliant: 'Failure to report to MBON violates COMAR 10.07.05.10(F).',
      unknown: 'Verify MBON reporting procedures are in place.',
    },
    stateNotes: [
      'Report immediately when action/inaction is known that may be grounds for action under Health Occupations Article §§8-316 and 8-6A-10',
      'Reporters have immunity under Courts and Judicial Proceedings Article §5-709',
    ],
  },

  // ============================================
  // SECTION H: ELECTRONIC VISIT VERIFICATION
  // ============================================
  {
    id: 'MD-H1',
    itemId: 'H1',
    stateCode: 'MD',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services',
    severity: 'high',
    regulationCode: '21st-Century-Cures',
    regulationName: 'EVV',
    agencyName: 'Maryland Department of Health - Medicaid',
    agencyAbbr: 'MDH',
    agencyPhone: '(410) 767-5800',
    agencyUrl: 'https://health.maryland.gov/mmcp/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a Maryland-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Maryland Medicaid-funded services.',
      atRisk: 'EVV is not being used consistently for all Medicaid visits. Federal law requires EVV for all personal care services.',
      nonCompliant: 'Maryland Medicaid personal care services require EVV per federal law. Failure to implement EVV can result in claim denials.',
      unknown: 'If you provide Medicaid services, verify EVV compliance.',
    },
    stateNotes: [
      'EVV required under 21st Century Cures Act for Medicaid personal care services',
      'Maryland uses a state-approved EVV vendor system',
      'Must capture: date, time in/out, location, services provided, provider ID',
      'Non-compliance can result in Medicaid claim denials',
      'Contact Maryland Medicaid at (410) 767-5800 for EVV requirements',
    ],
  },
];

export default MARYLAND_QUESTIONS;